import React from 'react';
import { Link } from 'react-router-dom';
import { EnvelopeIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import EmailSignup from './EmailSignup';

const CURRENT_YEAR = new Date().getFullYear();
const CONTACT_EMAIL = 'streetsaudio.support@gmail.com';

// Optional: set in .env for social links, e.g. REACT_APP_INSTAGRAM_URL, REACT_APP_X_URL
const INSTAGRAM_URL = process.env.REACT_APP_INSTAGRAM_URL || '';
const X_URL = process.env.REACT_APP_X_URL || '';

const footerLinks = [
  { name: 'Sample Packs', href: '/samples' },
  { name: 'Presets', href: '/presets' },
  { name: 'Templates', href: '/templates' },
  { name: 'Bundles', href: '/bundles' },
  { name: 'Best Sellers', href: '/best-sellers' },
  { name: 'Resources', href: '/resources' },
  { name: 'FAQ', href: '/faq' },
  { name: 'Contact', href: '/contact' },
];

const legalLinks = [
  { name: 'Licensing', href: '/licensing' },
  { name: 'Refund Policy', href: '/refund-policy' },
];

const Footer: React.FC = () => {
  return (
    <footer className="relative z-10 bg-black border-t border-white/5 mt-auto">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Stay in the loop + email signup */}
        <div className="mb-10 pb-8 border-b border-white/5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h3 className="text-sm font-semibold tracking-wider text-white mb-1">Stay in the loop</h3>
              <p className="text-sm text-gray-500 mb-3">New packs, presets, templates, and free samples. No spam.</p>
              <EmailSignup variant="footer" />
            </div>
            <div className="flex items-center gap-4">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-neon-red text-neon-red hover:bg-neon-red hover:text-white transition-colors text-sm font-medium"
              >
                <EnvelopeIcon className="h-4 w-4" />
                Email us
              </a>
              {INSTAGRAM_URL ? (
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                  aria-label="Instagram"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm0 5.25a5.25 5.25 0 100 10.5 5.25 5.25 0 000-10.5zM12 7a5 5 0 110 10 5 5 0 010-10z" clipRule="evenodd" />
                  </svg>
                </a>
              ) : null}
              {X_URL ? (
                <a
                  href={X_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-400 hover:text-white transition-colors"
                  aria-label="X (Twitter)"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </a>
              ) : null}
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          {/* Brand + tagline */}
          <div className="text-center md:text-left">
            <Link to="/" className="inline-flex items-center gap-2">
              <MusicalNoteIcon className="h-5 w-5 text-neon-red" />
              <span className="text-xl font-metal text-white tracking-wider hover:text-neon-red transition-colors duration-300">
                Streets Audio
              </span>
            </Link>
            <p className="mt-2 text-sm text-gray-500 max-w-xs">
              Premium sample packs and presets for modern producers. Royalty-free, ready for your next beat.
            </p>
          </div>

          {/* Links + contact */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-10">
            <nav className="flex flex-wrap justify-center md:justify-end gap-x-6 gap-y-1" aria-label="Footer">
              {footerLinks.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="inline-flex items-center justify-center md:justify-end gap-2 text-sm text-gray-400 hover:text-neon-red transition-colors"
              aria-label={`Email us at ${CONTACT_EMAIL}`}
            >
              <EnvelopeIcon className="h-4 w-4 flex-shrink-0" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-white/5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex flex-wrap justify-center sm:justify-start gap-x-6 gap-y-1">
              {legalLinks.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className="text-xs text-gray-500 hover:text-white transition-colors"
                >
                  {item.name}
                </Link>
              ))}
            </div>
            <p className="text-xs text-gray-600 text-center sm:text-right">
              © {CURRENT_YEAR} Streets Audio. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
